import React, { useState } from 'react';
import { CheckCircle, ChevronRight, ChevronLeft, UserPlus, Trash2 } from 'lucide-react';
import { Button, Input } from '../../ui';

const STEPS = [
  { id: 1, label: 'Tenant Info' },
  { id: 2, label: 'User Provisioning' },
  { id: 3, label: 'Feature Config' },
  { id: 4, label: 'Roles & Permissions' },
  { id: 5, label: 'Review & Submit' },
];

const FEATURES = [
  { key: 'assignments', label: 'Assignments' },
  { key: 'emailSignal', label: 'Email Signals' },
  { key: 'strategicInitiatives', label: 'Strategic Initiatives' },
  { key: 'recurringTasks', label: 'Recurring Tasks' },
];

const ROLES = ['Unit Manager', 'Service Line Director', 'System Administrator'];

const DEFAULT_ROLE_PERMISSIONS = {
  'Unit Manager':           { assignments: true,  emailSignal: false, strategicInitiatives: false, recurringTasks: true },
  'Service Line Director':  { assignments: true,  emailSignal: true,  strategicInitiatives: true,  recurringTasks: true },
  'System Administrator':   { assignments: true,  emailSignal: true,  strategicInitiatives: true,  recurringTasks: true },
};

function StepIndicator({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
      {STEPS.map((step, idx) => (
        <React.Fragment key={step.id}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: step.id < current ? 'var(--aqua)' : step.id === current ? 'var(--royal)' : 'var(--navy-light)',
              color: step.id <= current ? '#fff' : 'var(--text-muted)',
              fontSize: 13, fontWeight: 700,
            }}>
              {step.id < current ? <CheckCircle size={16} /> : step.id}
            </div>
            <div style={{ fontSize: 10, color: step.id === current ? 'var(--aqua-bright)' : 'var(--text-muted)', marginTop: 4, textAlign: 'center', whiteSpace: 'nowrap' }}>
              {step.label}
            </div>
          </div>
          {idx < STEPS.length - 1 && (
            <div style={{ flex: 1, height: 2, background: step.id < current ? 'var(--aqua)' : 'var(--navy-light)', margin: '0 4px', marginBottom: 20 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function TenantInfoStep({ data, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {[
        { label: 'Tenant Name *', key: 'name', placeholder: 'Riverside Health System', col: 2 },
        { label: 'Subdomain Slug *', key: 'subdomain', placeholder: 'riverside-health' },
        { label: 'Contract Tier', key: 'tier', type: 'select', options: ['Starter', 'Growth', 'Enterprise'] },
        { label: 'Go-Live Date', key: 'goLive', type: 'date' },
        { label: 'Primary Contact Name', key: 'contactName', placeholder: 'Jane Smith' },
        { label: 'Primary Contact Email', key: 'contactEmail', placeholder: 'jane@riversidehealth.com', type: 'email' },
      ].map(field => (
        <div key={field.key} style={{ gridColumn: field.col === 2 ? '1 / -1' : undefined }}>
          <label className="block text-[13px] font-medium text-text-dark mb-1.5">{field.label}</label>
          {field.type === 'select' ? (
            <select value={data[field.key] || ''} onChange={e => onChange(field.key, e.target.value)}
              className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" style={{ boxSizing: 'border-box' }}>
              <option value="">Select tier…</option>
              {field.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <Input type={field.type || 'text'} value={data[field.key] || ''} placeholder={field.placeholder}
              onChange={e => onChange(field.key, e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }} />
          )}
        </div>
      ))}
      <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" id="baa" checked={data.baaSigned || false} onChange={e => onChange('baaSigned', e.target.checked)} />
        <label htmlFor="baa" className="text-[13px] text-text-dark">BAA signed and on file</label>
      </div>
    </div>
  );
}

function UserProvisioningStep({ users, onAdd, onRemove, onChange }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="text-[13px] text-text-dark-secondary">Add initial users for this tenant.</div>
        <Button variant="primary" size="sm" onClick={onAdd} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <UserPlus size={14} /> Add User
        </Button>
      </div>
      {users.length === 0 && <div className="text-center text-text-dark-secondary text-[13px]" style={{ padding: 24 }}>No users added yet. Click "Add User" to start.</div>}
      {users.map((u, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 10, marginBottom: 10, alignItems: 'end' }}>
          {['name', 'email'].map(f => (
            <div key={f}>
              <label className="text-[11px] text-text-dark-secondary block mb-1">{f === 'name' ? 'Full Name' : 'Email'}</label>
              <input type={f === 'email' ? 'email' : 'text'} value={u[f]} placeholder={f === 'name' ? 'Jane Smith' : 'jane@example.com'}
                onChange={e => onChange(i, f, e.target.value)}
                className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" style={{ boxSizing: 'border-box' }} />
            </div>
          ))}
          <div>
            <label className="text-[11px] text-text-dark-secondary block mb-1">Role</label>
            <select value={u.role} onChange={e => onChange(i, 'role', e.target.value)}
              className="h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30">
              <option value="manager">Unit Manager</option>
              <option value="director">Director</option>
            </select>
          </div>
          <Button variant="destructive" size="sm" onClick={() => onRemove(i)} title="Remove user"><Trash2 size={15} /></Button>
        </div>
      ))}
      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" id="sendInvites" defaultChecked />
        <label htmlFor="sendInvites" className="text-[13px] text-text-dark">Send email invitations to all users upon submission</label>
      </div>
    </div>
  );
}

function FeatureConfigStep({ features, onChange }) {
  return (
    <div>
      <div className="text-[13px] text-text-dark-secondary" style={{ marginBottom: 16 }}>Enable or disable product features for this tenant.</div>
      {FEATURES.map(f => (
        <div key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 8, background: 'var(--surface-warm)', marginBottom: 8 }}>
          <div>
            <div className="text-[13px] font-medium text-text-dark">{f.label}</div>
          </div>
          <label style={{ position: 'relative', display: 'inline-block', width: 40, height: 22 }}>
            <input type="checkbox" checked={features[f.key] !== false} onChange={e => onChange(f.key, e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{
              position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 11,
              background: features[f.key] !== false ? 'var(--aqua)' : 'var(--navy-light)',
              transition: '0.2s',
            }}>
              <span style={{
                position: 'absolute', height: 16, width: 16, left: features[f.key] !== false ? 20 : 3, bottom: 3,
                background: '#fff', borderRadius: '50%', transition: '0.2s',
              }} />
            </span>
          </label>
        </div>
      ))}
    </div>
  );
}

function RolesPermissionsStep({ permissions, onChange }) {
  return (
    <div>
      <div className="text-[13px] text-text-dark-secondary" style={{ marginBottom: 16 }}>Set which features each role can access for this tenant.</div>
      <div style={{ overflowX: 'auto' }}>
        <table className="nn-table">
          <thead>
            <tr>
              <th>Role</th>
              {FEATURES.map(f => <th key={f.key} style={{ textAlign: 'center' }}>{f.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {ROLES.map(role => (
              <tr key={role}>
                <td style={{ fontWeight: 500 }}>{role}</td>
                {FEATURES.map(f => (
                  <td key={f.key} style={{ textAlign: 'center' }}>
                    <input type="checkbox"
                      checked={permissions[role]?.[f.key] ?? DEFAULT_ROLE_PERMISSIONS[role][f.key]}
                      onChange={e => onChange(role, f.key, e.target.checked)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewStep({ tenant, users, features, permissions }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Section title="Tenant Info">
        {[['Name', tenant.name], ['Subdomain', tenant.subdomain], ['Tier', tenant.tier], ['Go-Live', tenant.goLive], ['Contact', tenant.contactName], ['Email', tenant.contactEmail], ['BAA', tenant.baaSigned ? 'Signed ✓' : 'Not signed']].map(([k, v]) => v ? (
          <Row key={k} label={k} value={v} />
        ) : null)}
      </Section>
      <Section title={`Users (${users.length})`}>
        {users.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No users added</div> : users.map((u, i) => <Row key={i} label={u.name || '—'} value={u.email} />)}
      </Section>
      <Section title="Features">
        {FEATURES.map(f => <Row key={f.key} label={f.label} value={features[f.key] !== false ? 'Enabled' : 'Disabled'} valueColor={features[f.key] !== false ? 'var(--green)' : 'var(--text-muted)'} />)}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: 'var(--surface-warm)', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, valueColor }) {
  return (
    <div className="text-body" style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span className="text-text-muted">{label}</span>
      <span style={{ color: valueColor || 'var(--text-dark)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

export default function OnboardingWizard() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [tenant, setTenant] = useState({});
  const [users, setUsers] = useState([]);
  const [features, setFeatures] = useState({ assignments: true, emailSignal: false, strategicInitiatives: false, recurringTasks: true });
  const [permissions, setPermissions] = useState({});

  const handleTenantChange = (key, val) => setTenant(t => ({ ...t, [key]: val }));
  const addUser = () => setUsers(u => [...u, { name: '', email: '', role: 'manager' }]);
  const removeUser = (i) => setUsers(u => u.filter((_, idx) => idx !== i));
  const updateUser = (i, key, val) => setUsers(u => u.map((x, idx) => idx === i ? { ...x, [key]: val } : x));
  const handleFeature = (key, val) => setFeatures(f => ({ ...f, [key]: val }));
  const handlePermission = (role, feature, val) => setPermissions(p => ({ ...p, [role]: { ...(p[role] || DEFAULT_ROLE_PERMISSIONS[role]), [feature]: val } }));

  if (submitted) {
    return (
      <div className="text-center" style={{ padding: '60px 24px' }}>
        <CheckCircle size={48} style={{ color: 'var(--green)', margin: '0 auto 16px' }} />
        <h2 className="text-text-dark" style={{ marginBottom: 8 }}>Tenant onboarded successfully!</h2>
        <p className="text-text-dark-secondary" style={{ marginBottom: 24 }}>{tenant.name} has been set up and users will receive their invitations shortly.</p>
        <Button variant="primary" size="sm" onClick={() => { setSubmitted(false); setStep(1); setTenant({}); setUsers([]); }}>
          Onboard Another Tenant
        </Button>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ maxWidth: 720, margin: '0 auto' }}>
      <div className="dashboard-header">
        <h1 className="dashboard-title">Client Onboarding</h1>
        <p className="dashboard-subtitle">Set up a new tenant in {STEPS.length} steps.</p>
      </div>

      <StepIndicator current={step} />

      <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative" style={{ marginBottom: 20 }}>
        <div className="p-5">
        <h3 className="text-[14px] font-semibold text-text-dark" style={{ marginTop: 0, marginBottom: 20 }}>
          Step {step}: {STEPS[step - 1].label}
        </h3>

        {step === 1 && <TenantInfoStep data={tenant} onChange={handleTenantChange} />}
        {step === 2 && <UserProvisioningStep users={users} onAdd={addUser} onRemove={removeUser} onChange={updateUser} />}
        {step === 3 && <FeatureConfigStep features={features} onChange={handleFeature} />}
        {step === 4 && <RolesPermissionsStep permissions={permissions} onChange={handlePermission} />}
        {step === 5 && <ReviewStep tenant={tenant} users={users} features={features} permissions={permissions} />}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="default" size="sm" onClick={() => setStep(s => s - 1)} disabled={step === 1}
          style={{ flex: 'none', opacity: step === 1 ? 0.4 : 1, cursor: step === 1 ? 'not-allowed' : 'pointer' }}>
          <ChevronLeft size={14} /> Back
        </Button>

        {step < 5 ? (
          <Button variant="primary" size="sm" onClick={() => setStep(s => s + 1)}
            style={{ flex: 'none' }}>
            Next <ChevronRight size={14} />
          </Button>
        ) : (
          <Button variant="primary" size="sm" onClick={() => setSubmitted(true)}
            style={{ flex: 'none' }}>
            Submit &amp; Launch <CheckCircle size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}
